import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Language } from '../types';
import { t } from '../locales';

interface SessionEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentDate: Date;
  onSave: (newDate: Date) => void;
  language: Language;
}

const SessionEditModal: React.FC<SessionEditModalProps> = ({ isOpen, onClose, currentDate, onSave, language }) => {
  const [dateStr, setDateStr] = useState(() => {
    // Format for datetime-local input: YYYY-MM-DDThh:mm
    // We adjust for timezone to ensure the input shows the correct local time
    const tzOffset = currentDate.getTimezoneOffset() * 60000; 
    const localISOTime = (new Date(currentDate.getTime() - tzOffset)).toISOString().slice(0, 16);
    return localISOTime;
  });

  if (!isOpen) return null;

  const handleSave = () => {
    const newDate = new Date(dateStr);
    if (!isNaN(newDate.getTime())) {
        onSave(newDate);
        onClose();
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose}></div>
      <div className="bg-white dark:bg-gray-800 w-full max-w-sm rounded-2xl shadow-2xl p-6 relative z-10 animate-popIn border border-gray-100 dark:border-gray-700">
        <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-4">{t(language, 'editSession')}</h3>
        
        <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
            {t(language, 'selectTime')}
        </label>
        
        <input 
            type="datetime-local"
            value={dateStr}
            onChange={(e) => setDateStr(e.target.value)}
            className="w-full p-3 rounded-xl border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-emerald-500 outline-none transition-all mb-6"
        />

        <div className="flex gap-3">
            <button 
                onClick={onClose}
                className="flex-1 py-3 rounded-xl bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 font-bold hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
                {t(language, 'cancel')}
            </button>
            <button 
                onClick={handleSave}
                className="flex-1 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold shadow-lg transition-transform active:scale-95"
            >
                {t(language, 'update')}
            </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SessionEditModal;